import React, { useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import PageHeader from 'components/base/page-header';
import { ReactComponent as TrashIcon } from 'assets/icons/trash-icon.svg';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { formatCurrency, getFullName, handleApiError } from 'utils/functions';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import CurrencyInput from 'components/base/currency-input';
import FundWalletModal from 'components/modals/fund-wallet-modal';
import InsufficientTag from 'components/base/insufficient-tag';
import LocalTextArea from 'components/base/local-text-area';
import { DepartmentType } from 'models/department.model';
import { UserType } from 'models/user.model';
import { MemberListType } from 'models/member.model';

type GiftBulkMembersType = {
  amount: number;
  department: DepartmentType;
  user: UserType;
  user_id: string;
};
const GiftBulkMembers = () => {
  const {
    setHideSidebar,
    setBackLinkTitle,
    setIsBackLink,
    setBackLink,
    activeCompany,
    companyWalletDetails,
    refetchMonthlyStat
  } = useDashboardContext();
  const [step, setStep] = useState(1);
  const [note, setNote] = useState('');
  const [selectedPeople, setSelectedPeople] = useState<GiftBulkMembersType[]>([]);
  const [loading, setLoading] = useState(false);
  const companyId = activeCompany?.id;
  const balance = +(companyWalletDetails?.available_balance || 0);

  const [showFundWalletModal, setShowFundWalletModal] = useState(false);

  const toggleFundWallet = () => {
    setShowFundWalletModal(!showFundWalletModal);
  };

  useEffect(() => {
    setHideSidebar(true);
    setIsBackLink(true);
    setBackLink(ROUTE_KEYS.PEOPLE);
    setBackLinkTitle('Return to Dashboard');
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  useEffect(() => {
    const selectedPeopleJSON = localStorage.getItem(STORAGE_KEYS.SELECTED_PEOPLE);
    if (selectedPeopleJSON) {
      const selectedPeopleArray = JSON.parse(selectedPeopleJSON);
      const giftDetails = selectedPeopleArray.map((item: MemberListType) => ({
        amount: 0,
        department: item.department,
        user: item.user,
        user_id: item.user_id
      }));
      setSelectedPeople(giftDetails);

      localStorage.removeItem(STORAGE_KEYS.SELECTED_PEOPLE);
    }
  }, []);

  const handleDelete = (index: number) => {
    const filteredPeople = selectedPeople.filter((item, i) => i !== index);
    setSelectedPeople([...filteredPeople]);
  };

  const handleAmountChange = (amount: string, index: number) => {
    setSelectedPeople((prevSelectedPeople) => {
      const updatedSelectedPeople = [...prevSelectedPeople];
      updatedSelectedPeople[index].amount = amount === '' ? 0 : parseFloat(amount);
      return updatedSelectedPeople;
    });
  };

  const giftMembers = async () => {
    if (!companyId) return;
    const gift_data = selectedPeople?.map((item) => {
      return {
        user_id: item.user_id,
        amount: item.amount,
        note
      };
    });
    const data = {
      company_id: companyId,
      gift_data
    };
    setLoading(true);
    try {
      const res = await apiService.post(`gift/bulk`, { data });
      if (res.data) {
        toast(<ToastMessage type='success' message='Members gifted successfully' />);
        refetchMonthlyStat();
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount: number = selectedPeople.reduce((a, b) => a + b.amount, 0);
  const isDisabled =
    selectedPeople.length < 1 ||
    selectedPeople.some((item) => item.amount < 1) ||
    (step === 2 && totalAmount > Number(balance));

  return (
    <div className='flex flex-col justify-center items-center w-full'>
      <div className='max-w-[414px]'>
        <PageHeader title='Gift Member(s)' subtitle='Appreciate your employees by gifting them' />
        {step === 1 && (
          <React.Fragment>
            {selectedPeople?.length !== 0 &&
              selectedPeople?.map((item, index) => (
                <div
                  key={index}
                  className={`${
                    index === selectedPeople.length - 1 ? '' : 'border-b border-gray-13'
                  }  mb-4 `}>
                  <Input
                    design='bordered'
                    type='text'
                    label='Department'
                    name='department'
                    disabled
                    value={item.department?.name}
                    placeholder='e.g Accounting'
                  />
                  <div className='flex gap-4 items-center my-6'>
                    <Input
                      design='bordered'
                      type='text'
                      label='Member name'
                      name='name'
                      disabled
                      value={getFullName(item.user)}
                      placeholder='Donatus globus'
                    />
                    <CurrencyInput
                      label='Gift amount'
                      value={item.amount}
                      onChange={(amount) => handleAmountChange(amount, index)}
                      placeholder='50,000'
                    />
                    <div
                      onClick={() => handleDelete(index)}
                      className='border border-gray-13 w-[30px] h-[30px] rounded-full flex items-center justify-center cursor-pointer mt-7 -mr-10 px-1'>
                      <TrashIcon />
                    </div>
                  </div>
                </div>
              ))}
          </React.Fragment>
        )}

        {step === 2 && (
          <React.Fragment>
            <LocalTextArea
              label='Add a note (optional)'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className='mb-8'
            />

            <div className='bg-gray-17 rounded-md border border-gray-18 w-full h-32 flex flex-col justify-center items-center mb-10'>
              <p className='text-gray-9 mb-2 text-lg'>Total gift is</p>
              <p className='text-sm text-gray-10'>
                NGN{' '}
                <span className='text-4xl font-semibold'>
                  {formatCurrency(totalAmount, {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                    style: 'decimal'
                  })}
                </span>
              </p>
            </div>

            <p className='text-center mb-10 text-lg text-gray-9'>Proceed to gift members</p>

            <div className='flex justify-between border-2 border-red-5 rounded-md p-3 items-center cursor-pointer'>
              <div>
                <p className='text-gray-10 font-medium'>Food Court Wallet</p>
                <p className='text-gray-9 text-sm'>Balance: {formatCurrency(balance)}</p>
              </div>
              <div>
                <InsufficientTag insufficient={totalAmount > Number(balance)} />
              </div>
            </div>
            {totalAmount > Number(balance) && (
              <div
                onClick={toggleFundWallet}
                className='text-blue-8 text-sm mt-2 cursor-pointer font-medium'>
                + Fund wallet
              </div>
            )}
          </React.Fragment>
        )}

        <FundWalletModal isOpen={showFundWalletModal} onClose={toggleFundWallet} />
        <div>
          <Button
            color='primary'
            variant='block'
            small
            loading={loading}
            disabled={isDisabled}
            onClick={step === 1 ? () => setStep(2) : giftMembers}
            className='w-[414px] mt-10 mb-4'>
            {step === 1 ? 'Continue' : step === 2 ? 'Confirm' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GiftBulkMembers;
